/**
 * AnnouncementBanner — renders all active announcements pushed by the master.
 * Fetches /api/announcements/active on mount, lets the user dismiss each one.
 */
import React, { useEffect, useState } from 'react';
import { X, Info, AlertTriangle, AlertOctagon } from 'lucide-react';
import apiClient from '../api/client';

const STYLES = {
  info:     { cls: 'bg-sky-500/15 text-sky-100 border-sky-500/40',     Icon: Info },
  warning:  { cls: 'bg-amber-500/15 text-amber-100 border-amber-500/40', Icon: AlertTriangle },
  critical: { cls: 'bg-rose-500/20 text-rose-100 border-rose-500/40',    Icon: AlertOctagon },
};

export default function AnnouncementBanner() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    apiClient.get('/api/announcements/active')
      .then((r) => setItems(r.data || []))
      .catch(() => {});
  }, []);

  const dismiss = async (id) => {
    setItems((it) => it.filter((a) => a.id !== id));
    try { await apiClient.post(`/api/announcements/${id}/dismiss`); } catch { /* ignore */ }
  };

  if (items.length === 0) return null;

  return (
    <div className="space-y-1" data-testid="announcement-stack">
      {items.map((a) => {
        const s = STYLES[a.severity] || STYLES.info;
        const Icon = s.Icon;
        return (
          <div
            key={a.id}
            className={`flex items-center justify-between gap-3 px-4 py-2 border-b ${s.cls}`}
            data-testid={`announcement-${a.id}`}
          >
            <div className="flex items-center gap-2 text-sm flex-1 min-w-0">
              <Icon className="w-4 h-4 shrink-0" />
              <span className="truncate">{a.message}</span>
            </div>
            <button
              onClick={() => dismiss(a.id)}
              className="p-1 hover:bg-black/20 rounded shrink-0"
              aria-label="Dismiss"
              data-testid={`announcement-dismiss-${a.id}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
